import { LitElement, html, css } from 'lit';

class MetalyxPage extends LitElement {
  static properties = {
    _activeTab: { type: String, state: true },
    _activePillar: { type: String, state: true },
  };

  static styles = css`
    :host {
      display: block;
      --mx-gradient: linear-gradient(135deg, #DDD6FE 0%, #C4B5FD 15%, #A78BFA 30%, #8B5CF6 45%, #7C3AED 60%, #6D28D9 75%, #5B21B6 90%, #4C1D95 100%);
      --mx-light: #DDD6FE;
      --mx-mid: #8B5CF6;
      --mx-primary: #7C3AED;
      --mx-deep: #4C1D95;
      --mx-darkest: #2E1065;
    }

    /* ─── HERO ─── */
    .hero {
      padding: 8rem var(--space-container, 1.5rem) 5rem;
      position: relative;
      overflow: hidden;
      text-align: center;
      background: var(--color-bg-primary, #0A0E1A);
    }

    .hero-bg {
      position: absolute;
      inset: 0;
      background:
        radial-gradient(ellipse 80% 50% at 50% 0%, rgba(124, 58, 237, 0.12) 0%, transparent 60%),
        radial-gradient(ellipse 60% 40% at 20% 80%, rgba(139, 92, 246, 0.06) 0%, transparent 50%),
        radial-gradient(ellipse 50% 40% at 80% 60%, rgba(93, 33, 182, 0.06) 0%, transparent 50%);
    }

    .hero-grid {
      position: absolute;
      inset: 0;
      background-image:
        linear-gradient(rgba(124, 58, 237, 0.04) 1px, transparent 1px),
        linear-gradient(90deg, rgba(124, 58, 237, 0.04) 1px, transparent 1px);
      background-size: 60px 60px;
      mask-image: radial-gradient(ellipse at center, black 20%, transparent 70%);
      -webkit-mask-image: radial-gradient(ellipse at center, black 20%, transparent 70%);
    }

    .hero-content {
      position: relative;
      z-index: 2;
      max-width: var(--max-width, 1200px);
      margin: 0 auto;
    }

    .hero-icon {
      width: 80px;
      height: 80px;
      margin: 0 auto 2rem;
    }

    .hero-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: clamp(2.5rem, 6vw, 4rem);
      font-weight: 800;
      color: var(--color-text-heading, #fff);
      margin: 0 0 1rem;
      letter-spacing: -0.03em;
      line-height: 1.1;
    }
    .hero-title .gradient-text {
      background: var(--mx-gradient);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    .hero-tagline {
      font-family: var(--font-sans, 'DM Sans', sans-serif);
      font-size: clamp(1rem, 2vw, 1.25rem);
      font-weight: 300;
      color: var(--color-text-muted, #64748B);
      max-width: 640px;
      margin: 0 auto 2.5rem;
      line-height: 1.7;
    }

    .hero-ctas {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      flex-wrap: wrap;
    }

    .hero-divider {
      width: 60px;
      height: 3px;
      background: var(--mx-gradient);
      border-radius: 2px;
      margin: 0 auto 2rem;
    }

    /* ─── SHARED SECTION ─── */
    .section {
      padding: var(--space-section, 6rem) var(--space-container, 1.5rem);
      max-width: var(--max-width, 1200px);
      margin: 0 auto;
    }

    .section-alt {
      padding: var(--space-section, 6rem) var(--space-container, 1.5rem);
      background: var(--color-bg-secondary, #111827);
    }
    .section-alt .section-inner {
      max-width: var(--max-width, 1200px);
      margin: 0 auto;
    }

    .section-label {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.65rem;
      letter-spacing: 0.25em;
      text-transform: uppercase;
      color: var(--mx-light);
      margin-bottom: 0.75rem;
      text-align: center;
    }

    .section-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: clamp(1.75rem, 4vw, 2.5rem);
      font-weight: 700;
      color: var(--color-text-heading, #fff);
      text-align: center;
      margin: 0 0 0.75rem;
      letter-spacing: -0.02em;
      line-height: 1.15;
    }

    .section-subtitle {
      font-family: var(--font-sans, 'DM Sans', sans-serif);
      font-size: var(--text-lg, 1.125rem);
      color: var(--color-text-muted, #64748B);
      text-align: center;
      max-width: 640px;
      margin: 0 auto 3rem;
      line-height: 1.7;
    }

    .gradient-line {
      width: 48px;
      height: 3px;
      background: var(--mx-gradient);
      border-radius: 2px;
      margin: 0 auto 1rem;
    }

    .divider {
      width: 100%;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(124, 58, 237, 0.2), transparent);
    }

    /* ─── MISSION ─── */
    .mission {
      font-family: var(--font-sans, 'DM Sans', sans-serif);
      font-size: clamp(1.05rem, 2vw, 1.2rem);
      color: var(--color-text-primary, #CBD5E1);
      text-align: center;
      max-width: 740px;
      margin: 0 auto 4rem;
      line-height: 1.8;
      font-weight: 300;
    }

    /* ─── CUBE PILLARS ─── */
    .pillars-layout {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      align-items: center;
    }

    .cube-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .cube-svg {
      width: 100%;
      max-width: 400px;
    }

    .cube-facet {
      cursor: pointer;
      transition: all var(--transition, 200ms ease);
    }
    .cube-facet .facet-fill {
      transition: all var(--transition, 200ms ease);
    }
    .cube-facet .facet-overlay {
      fill: transparent;
      transition: fill var(--transition, 200ms ease);
    }
    .cube-facet:hover .facet-overlay {
      fill: rgba(196, 181, 253, 0.2);
    }
    .cube-facet.active .facet-overlay {
      fill: rgba(221, 214, 254, 0.45);
    }
    .cube-facet.active .facet-fill {
      filter: brightness(1.6) saturate(1.3);
    }

    .cube-label {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 11px;
      letter-spacing: 0.12em;
      text-transform: uppercase;
      fill: rgba(255,255,255,0.3);
      text-anchor: middle;
      pointer-events: none;
      transition: fill var(--transition, 200ms ease);
    }
    .cube-facet:hover .cube-label { fill: rgba(255,255,255,0.8); }
    .cube-facet.active .cube-label { fill: #FFFFFF; font-weight: 700; }

    .cube-num {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: 18px;
      font-weight: 800;
      fill: rgba(255,255,255,0.1);
      text-anchor: middle;
      pointer-events: none;
    }
    .cube-facet.active .cube-num { fill: rgba(255,255,255,0.4); }

    /* Breathing pulse on active facet */
    @keyframes facetPulse {
      0%, 100% { opacity: 0.35; }
      50% { opacity: 0.55; }
    }
    .cube-facet.active .facet-overlay {
      animation: facetPulse 2.4s ease-in-out infinite;
    }

    .pillar-detail {
      min-height: 280px;
    }
    .pillar-detail-num {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: 3rem;
      font-weight: 800;
      background: var(--mx-gradient);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      opacity: 0.25;
      line-height: 1;
      margin-bottom: 0.5rem;
    }
    .pillar-detail-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-2xl, 1.5rem);
      font-weight: 700;
      color: var(--color-text-heading, #fff);
      margin-bottom: 0.75rem;
    }
    .pillar-detail-desc {
      font-size: var(--text-base, 1rem);
      color: var(--color-text-muted, #64748B);
      line-height: 1.7;
      margin-bottom: 1.25rem;
    }
    .pillar-detail-list {
      list-style: none;
      padding: 0; margin: 0;
      display: grid; gap: 0.5rem;
    }
    .pillar-detail-list li {
      display: flex;
      align-items: flex-start;
      gap: 0.6rem;
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-primary, #CBD5E1);
      line-height: 1.5;
    }
    .pillar-detail-list li::before {
      content: '';
      width: 6px; height: 6px;
      border-radius: 50%;
      background: var(--mx-light);
      flex-shrink: 0;
      margin-top: 0.45rem;
    }

    @media (max-width: 1024px) {
      .pillars-layout { grid-template-columns: 1fr; }
      .cube-svg { max-width: 340px; }
    }

    /* ─── DIFFERENTIATORS ─── */
    .diff-grid {
      display: grid;
      grid-template-columns: repeat(5, 1fr);
      gap: 1.25rem;
    }

    .diff-card {
      background: var(--color-bg-card, #1E293B);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: var(--radius-lg, 1rem);
      padding: 1.75rem 1.5rem;
      text-align: center;
      transition: all var(--transition, 200ms ease);
      position: relative;
      overflow: hidden;
    }
    .diff-card::before {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0;
      height: 2px;
      background: var(--mx-gradient);
      opacity: 0;
      transition: opacity var(--transition, 200ms ease);
    }
    .diff-card:hover {
      transform: translateY(-3px);
      border-color: rgba(124, 58, 237, 0.2);
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
    }
    .diff-card:hover::before { opacity: 1; }

    .diff-icon {
      width: 44px; height: 44px;
      border-radius: var(--radius-md, 0.75rem);
      background: rgba(124, 58, 237, 0.12);
      display: flex; align-items: center; justify-content: center;
      margin: 0 auto 1rem;
      color: var(--mx-light);
    }

    .diff-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-sm, 0.875rem);
      font-weight: 600;
      color: var(--color-text-heading, #fff);
      margin-bottom: 0.5rem;
    }

    .diff-text {
      font-size: var(--text-xs, 0.75rem);
      color: var(--color-text-muted, #64748B);
      line-height: 1.6;
    }

    /* ─── OFFERINGS ─── */
    .offerings-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 1.5rem;
    }

    .offering-card {
      background: var(--color-bg-card, #1E293B);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: var(--radius-lg, 1rem);
      padding: 2rem 1.75rem;
      transition: all var(--transition, 200ms ease);
    }
    .offering-card:hover {
      transform: translateY(-3px);
      border-color: rgba(124, 58, 237, 0.2);
      box-shadow: 0 8px 30px rgba(0,0,0,0.3);
    }

    .offering-num {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-weight: 800; font-size: 2.5rem; line-height: 1;
      margin-bottom: 0.75rem;
      background: var(--mx-gradient);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      opacity: 0.3;
    }

    .offering-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-xl, 1.25rem); font-weight: 700;
      color: var(--color-text-heading, #fff);
      margin-bottom: 0.75rem;
    }

    .offering-text {
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-muted, #64748B);
      line-height: 1.7;
    }

    /* ─── ARCHITECTURE ─── */
    .arch-container {
      display: grid;
      grid-template-columns: 190px 40px 1fr 40px 190px;
      gap: 0.75rem; align-items: center;
    }

    .arch-col { display: flex; flex-direction: column; gap: 0.4rem; }

    .arch-col-label {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.6rem; letter-spacing: 0.2em;
      text-transform: uppercase; text-align: center;
      margin-bottom: 0.5rem; font-weight: 600;
    }
    .arch-col-label.sources { color: var(--mx-light); }
    .arch-col-label.outputs { color: var(--mx-mid); }

    .arch-group {
      border: 1px solid rgba(124, 58, 237, 0.1);
      border-radius: var(--radius-md, 0.75rem);
      padding: 0.5rem;
      display: flex; flex-direction: column; gap: 0.35rem;
    }
    .arch-group-label {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.5rem; letter-spacing: 0.15em;
      text-transform: uppercase;
      color: var(--color-text-muted, #64748B);
      text-align: center; padding-bottom: 0.2rem;
    }

    .arch-item {
      background: var(--color-bg-card, #1E293B);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: var(--radius-sm, 0.375rem);
      padding: 0.55rem 0.75rem;
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.6rem;
      color: var(--color-text-primary, #CBD5E1);
      letter-spacing: 0.03em;
      display: flex; align-items: center; gap: 0.5rem;
      transition: all var(--transition, 200ms ease);
    }
    .arch-item:hover {
      border-color: rgba(124, 58, 237, 0.25);
      background: rgba(30, 41, 59, 0.8);
    }

    .arch-dot {
      width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0;
    }
    .arch-dot.light { background: var(--mx-light); }
    .arch-dot.mid { background: var(--mx-mid); }
    .arch-dot.primary { background: var(--mx-primary); }
    .arch-dot.deep { background: var(--mx-deep); }

    .arch-arrows {
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      height: 100%; position: relative;
    }
    .arch-arrows::before {
      content: '';
      position: absolute; top: 10%; bottom: 10%;
      width: 2px;
      background: linear-gradient(to bottom, transparent, rgba(124, 58, 237, 0.3), rgba(139, 92, 246, 0.3), transparent);
    }
    .arch-arrow-icon {
      color: var(--mx-light); opacity: 0.5;
      font-size: 1.1rem; position: relative; z-index: 1; line-height: 1;
    }

    .arch-core {
      background: var(--color-bg-card, #1E293B);
      border: 1px solid rgba(124, 58, 237, 0.25);
      border-radius: var(--radius-lg, 1rem);
      padding: 1.75rem 1.5rem;
      position: relative; overflow: hidden;
    }
    .arch-core::before {
      content: '';
      position: absolute; top: 0; left: 0; right: 0;
      height: 2px;
      background: var(--mx-gradient);
    }
    .arch-core::after {
      content: '';
      position: absolute; inset: 0;
      background: radial-gradient(ellipse at center, rgba(124, 58, 237, 0.06) 0%, transparent 70%);
      pointer-events: none;
    }

    .arch-core-header {
      display: flex; align-items: center; justify-content: center;
      gap: 0.6rem; margin-bottom: 1.25rem;
      position: relative; z-index: 1;
    }
    .arch-core-logo { width: 28px; height: 28px; flex-shrink: 0; }
    .arch-core-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-lg, 1.125rem); font-weight: 700;
      color: var(--color-text-heading, #fff);
    }

    .arch-core-grid {
      display: grid; grid-template-columns: repeat(5, 1fr);
      gap: 0.5rem; position: relative; z-index: 1;
    }
    .arch-core-item {
      background: rgba(124, 58, 237, 0.1);
      border: 1px solid rgba(124, 58, 237, 0.15);
      border-radius: var(--radius-sm, 0.375rem);
      padding: 0.65rem 0.4rem; text-align: center;
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.55rem; color: var(--mx-light);
      letter-spacing: 0.03em;
      transition: all var(--transition, 200ms ease);
    }
    .arch-core-item:hover {
      background: rgba(124, 58, 237, 0.18);
      border-color: rgba(124, 58, 237, 0.3);
    }

    .arch-flow-strip {
      margin-top: 2.5rem;
      display: flex; align-items: center; justify-content: center;
      gap: 0;
    }
    .arch-flow-step {
      display: flex; align-items: center; gap: 0;
    }
    .arch-flow-label {
      padding: 0.6rem 1.5rem;
      background: var(--color-bg-card, #1E293B);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.6rem;
      color: var(--color-text-muted, #64748B);
      letter-spacing: 0.05em; white-space: nowrap;
      transition: all var(--transition, 200ms ease);
      position: relative;
    }
    .arch-flow-label:first-child { border-radius: 100px 0 0 100px; }
    .arch-flow-label:last-child { border-radius: 0 100px 100px 0; }
    .arch-flow-label:hover {
      color: var(--color-text-primary, #CBD5E1);
      background: rgba(124, 58, 237, 0.08);
    }
    .arch-flow-label.active {
      background: rgba(124, 58, 237, 0.15);
      border-color: rgba(124, 58, 237, 0.3);
      color: var(--mx-light);
      font-weight: 700;
    }
    .arch-flow-label .flow-icon {
      display: block;
      font-size: 0.85rem;
      margin-bottom: 0.15rem;
      text-align: center;
    }
    .arch-flow-chevron {
      color: var(--mx-primary); opacity: 0.5;
      font-size: 1rem; padding: 0 0.1rem;
      font-weight: 700;
    }

    /* ─── FEATURES ─── */
    .feature-tabs {
      display: flex; justify-content: center; gap: 0.5rem;
      flex-wrap: wrap; margin-bottom: 2.5rem;
    }
    .feature-tab {
      padding: 0.5rem 1.25rem;
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: 100px; background: transparent;
      color: var(--color-text-muted, #64748B);
      font-family: var(--font-sans, 'DM Sans', sans-serif);
      font-size: var(--text-sm, 0.875rem); font-weight: 500;
      cursor: pointer;
      transition: all var(--transition, 200ms ease);
    }
    .feature-tab:hover {
      border-color: rgba(124, 58, 237, 0.3);
      color: var(--color-text-primary, #CBD5E1);
    }
    .feature-tab.active {
      background: rgba(124, 58, 237, 0.15);
      border-color: rgba(124, 58, 237, 0.4);
      color: var(--mx-light);
    }

    .feature-panel {
      display: grid; grid-template-columns: 1fr 1fr;
      gap: 3rem; align-items: center;
    }
    .feature-info h3 {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-2xl, 1.5rem); font-weight: 700;
      color: var(--color-text-heading, #fff);
      margin: 0 0 0.75rem;
    }
    .feature-info .feature-desc {
      font-size: var(--text-base, 1rem);
      color: var(--color-text-muted, #64748B);
      line-height: 1.7; margin-bottom: 1.5rem;
    }
    .feature-list {
      list-style: none; padding: 0; margin: 0;
      display: grid; gap: 0.6rem;
    }
    .feature-list li {
      display: flex; align-items: flex-start; gap: 0.6rem;
      font-size: var(--text-sm, 0.875rem);
      color: var(--color-text-primary, #CBD5E1); line-height: 1.5;
    }
    .feature-list li::before {
      content: '';
      width: 6px; height: 6px; border-radius: 50%;
      background: var(--mx-light);
      flex-shrink: 0; margin-top: 0.45rem;
    }

    .feature-mockup {
      background: var(--color-bg-primary, #0A0E1A);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: var(--radius-lg, 1rem);
      padding: 1.25rem; position: relative;
    }
    .mockup-header { display: flex; gap: 0.4rem; margin-bottom: 1rem; }
    .dot { width: 8px; height: 8px; border-radius: 50%; }
    .dot-r { background: #EF4444; }
    .dot-y { background: #F59E0B; }
    .dot-g { background: #10b981; }

    .mockup-body { display: grid; gap: 0.75rem; }
    .mockup-bar { height: 6px; border-radius: 3px; background: var(--color-bg-card, #1E293B); }
    .mockup-bar.accent { background: linear-gradient(90deg, var(--mx-deep), var(--mx-light)); }

    .mockup-chart {
      height: 140px;
      background: var(--color-bg-card, #1E293B);
      border-radius: var(--radius-sm, 0.375rem);
      display: flex; align-items: flex-end; gap: 3px; padding: 0.75rem;
    }
    .chart-bar { flex: 1; border-radius: 2px 2px 0 0; min-height: 6px; }
    .chart-bar.purple { background: linear-gradient(to top, var(--mx-primary), rgba(124, 58, 237, 0.3)); }
    .chart-bar.light { background: linear-gradient(to top, var(--mx-mid), rgba(139, 92, 246, 0.3)); }

    .mockup-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; }
    .stat-box {
      background: var(--color-bg-card, #1E293B);
      border-radius: var(--radius-sm, 0.375rem);
      padding: 0.75rem; text-align: center;
    }
    .stat-val {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: var(--text-lg, 1.125rem); font-weight: 700;
      background: var(--mx-gradient);
      -webkit-background-clip: text; -webkit-text-fill-color: transparent;
      background-clip: text;
    }
    .stat-lbl { font-size: 0.65rem; color: var(--color-text-muted, #64748B); margin-top: 0.2rem; }

    .mockup-table { width: 100%; border-collapse: collapse; }
    .mockup-table th {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.6rem; color: var(--mx-light); text-align: left;
      padding: 0.4rem 0.6rem;
      border-bottom: 1px solid rgba(124, 58, 237, 0.15);
      letter-spacing: 0.05em; text-transform: uppercase;
    }
    .mockup-table td {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.6rem; color: var(--color-text-primary, #CBD5E1);
      padding: 0.35rem 0.6rem;
      border-bottom: 1px solid rgba(255,255,255,0.03);
    }
    .mockup-table .val-green { color: #10b981; }
    .mockup-table .val-amber { color: #F59E0B; }

    .mockup-compliance { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; }
    .compliance-item {
      background: var(--color-bg-card, #1E293B);
      border-radius: var(--radius-sm, 0.375rem);
      padding: 0.6rem 0.75rem;
      display: flex; align-items: center; gap: 0.5rem;
    }
    .compliance-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
    .compliance-dot.pass { background: #10b981; }
    .compliance-dot.warn { background: #F59E0B; }
    .compliance-dot.fail { background: #EF4444; }
    .compliance-label {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.6rem; color: var(--color-text-primary, #CBD5E1);
    }

    /* ─── BENEFITS ─── */
    .benefits-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1.25rem; }
    .benefit-card {
      background: var(--color-bg-card, #1E293B);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: var(--radius-lg, 1rem);
      padding: 1.75rem;
      transition: all var(--transition, 200ms ease);
    }
    .benefit-card:hover {
      transform: translateY(-2px);
      border-color: rgba(124, 58, 237, 0.2);
    }
    .benefit-icon {
      width: 36px; height: 36px;
      border-radius: var(--radius-sm, 0.375rem);
      background: rgba(124, 58, 237, 0.1);
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 0.75rem; font-size: 1rem;
    }
    .benefit-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-sm, 0.875rem); font-weight: 600;
      color: var(--color-text-heading, #fff); margin-bottom: 0.4rem;
    }
    .benefit-text {
      font-size: var(--text-xs, 0.75rem);
      color: var(--color-text-muted, #64748B); line-height: 1.6;
    }

    /* ─── TIMELINE ─── */
    .timeline {
      display: grid; grid-template-columns: repeat(5, 1fr);
      position: relative; margin-top: 1rem;
    }
    .timeline::before {
      content: ''; position: absolute;
      top: 20px; left: 10%; right: 10%;
      height: 2px; background: var(--mx-gradient); opacity: 0.3;
    }
    .timeline-step { text-align: center; position: relative; padding: 0 0.5rem; }
    .timeline-dot {
      width: 12px; height: 12px; border-radius: 50%;
      background: var(--mx-gradient);
      margin: 14px auto 1.25rem; position: relative; z-index: 1;
    }
    .timeline-week {
      font-family: var(--font-mono, 'Space Mono', monospace);
      font-size: 0.55rem; color: var(--mx-light);
      letter-spacing: 0.15em; text-transform: uppercase; margin-bottom: 0.5rem;
    }
    .timeline-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-xs, 0.75rem); font-weight: 600;
      color: var(--color-text-heading, #fff); margin-bottom: 0.5rem;
    }
    .timeline-items { list-style: none; padding: 0; margin: 0; }
    .timeline-items li {
      font-size: 0.65rem; color: var(--color-text-muted, #64748B);
      line-height: 1.5; padding: 0.15rem 0;
    }

    /* ─── BESPOKE ─── */
    .bespoke-layout {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 3rem;
      align-items: center;
    }
    .bespoke-text p {
      font-size: var(--text-base, 1rem);
      color: var(--color-text-muted, #64748B);
      line-height: 1.8; margin: 0 0 1.25rem;
    }
    .bespoke-text p:last-child { margin-bottom: 0; }
    .bespoke-text strong {
      color: var(--color-text-primary, #CBD5E1);
      font-weight: 600;
    }

    .bespoke-cards {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 1rem;
    }
    .bespoke-card {
      background: var(--color-bg-card, #1E293B);
      border: 1px solid var(--color-border, rgba(255,255,255,0.06));
      border-radius: var(--radius-md, 0.75rem);
      padding: 1.25rem;
      transition: all var(--transition, 200ms ease);
    }
    .bespoke-card:hover {
      border-color: rgba(124, 58, 237, 0.2);
      transform: translateY(-2px);
    }
    .bespoke-card-icon {
      font-size: 1.25rem;
      margin-bottom: 0.5rem;
    }
    .bespoke-card-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: var(--text-sm, 0.875rem);
      font-weight: 600;
      color: var(--color-text-heading, #fff);
      margin-bottom: 0.3rem;
    }
    .bespoke-card-text {
      font-size: var(--text-xs, 0.75rem);
      color: var(--color-text-muted, #64748B);
      line-height: 1.5;
    }

    @media (max-width: 1024px) {
      .bespoke-layout { grid-template-columns: 1fr; gap: 2rem; }
    }
    @media (max-width: 640px) {
      .bespoke-cards { grid-template-columns: 1fr; }
    }

    /* ─── CLIENTS ─── */
    .clients-grid {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 3.5rem;
      flex-wrap: wrap;
    }
    .client-logo {
      height: 44px;
      width: auto;
      object-fit: contain;
      opacity: 0.55;
      transition: opacity var(--transition, 200ms ease);
      filter: grayscale(100%) brightness(1.8);
    }
    .client-logo:hover {
      opacity: 0.85;
      filter: grayscale(50%) brightness(1.8);
    }
    .client-logo.white-bg {
      mix-blend-mode: lighten;
      opacity: 0.5;
    }
    .client-logo.white-bg:hover {
      opacity: 0.75;
    }

    /* ─── CTA ─── */
    .cta-section {
      text-align: center;
      padding: var(--space-section, 6rem) var(--space-container, 1.5rem);
      position: relative; overflow: hidden;
    }
    .cta-bg {
      position: absolute; inset: 0;
      background: radial-gradient(ellipse 60% 50% at 50% 100%, rgba(124, 58, 237, 0.08) 0%, transparent 60%);
    }
    .cta-content { position: relative; z-index: 1; }
    .cta-title {
      font-family: var(--font-heading, 'Outfit', sans-serif);
      font-size: clamp(1.75rem, 4vw, 2.5rem); font-weight: 700;
      color: var(--color-text-heading, #fff); margin: 0 0 1rem;
    }
    .cta-text {
      font-size: var(--text-lg, 1.125rem);
      color: var(--color-text-muted, #64748B);
      margin: 0 auto 2rem; max-width: 500px; line-height: 1.7;
    }

    /* ─── RESPONSIVE ─── */
    @media (max-width: 1024px) {
      .diff-grid { grid-template-columns: repeat(3, 1fr); }
      .offerings-grid { grid-template-columns: repeat(2, 1fr); }
      .arch-container { grid-template-columns: 1fr; gap: 0.75rem; }
      .arch-arrows {
        flex-direction: row; height: auto; padding: 0.25rem 0;
      }
      .arch-arrows::before {
        top: 50%; bottom: auto; left: 10%; right: 10%;
        width: auto; height: 2px;
        background: linear-gradient(to right, transparent, rgba(124, 58, 237, 0.3), rgba(139, 92, 246, 0.3), transparent);
      }
      .arch-core-grid { grid-template-columns: repeat(3, 1fr); }
      .arch-flow-strip { flex-wrap: wrap; gap: 0.5rem; }
      .arch-flow-label { border-radius: var(--radius-sm, 0.375rem) !important; }
      .arch-flow-label { border-radius: var(--radius-sm, 0.375rem) !important; }
      .feature-panel { grid-template-columns: 1fr; gap: 2rem; }
      .benefits-grid { grid-template-columns: repeat(2, 1fr); }
      .timeline { grid-template-columns: 1fr; gap: 1.5rem; }
      .timeline::before {
        top: 0; bottom: 0; left: 20px; right: auto; width: 2px; height: auto;
      }
      .timeline-step { text-align: left; padding-left: 3rem; }
      .timeline-dot { position: absolute; left: 14px; top: 0; margin: 0; }
    }

    @media (max-width: 640px) {
      .diff-grid { grid-template-columns: 1fr 1fr; }
      .offerings-grid { grid-template-columns: 1fr; }
      .benefits-grid { grid-template-columns: 1fr; }
    }
  `;

  constructor() {
    super();
    this._activeTab = 'dashboards';
    this._activePillar = 'collect';
  }

  _setPillar(p) {
    this._activePillar = p;
  }

  get _pillarData() {
    return {
      collect: {
        num: '01',
        title: 'Collect',
        desc: 'Acquire data from every operational source — from SCADA historians and lab systems to manual capture and drill hole databases.',
        items: [
          'Direct connections to historians and SCADA',
          'Lab and LIMS integration',
          'Weighbridge and dispatch systems',
          'Manual data capture with validation',
          'Drilling, survey, and geological data',
          'ERP and financial system feeds',
        ],
      },
      integrate: {
        num: '02',
        title: 'Integrate',
        desc: 'Unify disparate data sources into a single, consistent data warehouse — resolving conflicts, filling gaps, and standardising formats.',
        items: [
          'Automated ETL pipelines',
          'Data quality checks and validation rules',
          'Time-series alignment across sources',
          'Master data management',
          'Conflict resolution and deduplication',
        ],
      },
      process: {
        num: '03',
        title: 'Process',
        desc: 'Automate calculations, reconciliations, and metal accounting — turning raw data into structured, auditable operational records.',
        items: [
          'Automated mass balancing',
          'Metal accounting calculations',
          'Reconciliation engine',
          'Configurable calculation framework',
          'Shift, daily, weekly, and monthly rollups',
        ],
      },
      analyse: {
        num: '04',
        title: 'Analyse',
        desc: 'Apply advanced analytics, machine learning, and AI to uncover patterns, predict outcomes, and identify optimisation opportunities.',
        items: [
          'Correlation and regression analysis',
          'Digital twins of plant processes',
          'Outlier and anomaly detection',
          'Recovery and grade forecasting',
          'Control charts and statistical monitoring',
        ],
      },
      deliver: {
        num: '05',
        title: 'Deliver',
        desc: 'Present insights through real-time dashboards, automated reports, and proactive notifications — reaching every stakeholder.',
        items: [
          'Interactive real-time dashboards',
          'Automated PDF and Excel reports',
          'Telegram, WhatsApp, and email alerts',
          'Event-based notifications',
          'Role-based access and views',
        ],
      },
      optimise: {
        num: '06',
        title: 'Optimise',
        desc: 'Drive continuous improvement through data-driven decision-making, closing the loop from insight back to operational action.',
        items: [
          'KPI tracking against targets',
          'Process optimisation recommendations',
          'Downtime and reliability analysis',
          'Compliance monitoring and scoring',
          'Continuous feedback loops',
        ],
      },
    };
  }

  _setTab(tab) {
    this._activeTab = tab;
  }

  _renderFeaturePanel() {
    const panels = {
      dashboards: {
        title: 'Live Dashboards',
        desc: 'Real-time dashboard architecture with direct historian connections and high-frequency data warehouse updates for instant insights — including reconciliations with lab data once available.',
        items: [
          'Real-time & historical dashboards',
          'Direct integration with data sources',
          'Process flow diagrams with live data',
          'Multiple chart types supported',
          'Fast, user-friendly, and efficient',
        ],
        mockup: 'chart',
      },
      accounting: {
        title: 'Metal Accounting',
        desc: 'Fully automated metal accounting solutions from mine to market. Track actuals, budgets, and forecasts with mass balancing, reconciliations, and complete audit trails.',
        items: [
          'Automated reconciliations & mass balancing',
          'Track actuals, budgets, and forecasts',
          'Change & audit tracking for every data point',
          'Capture any timeframe — shift, day, week',
          'Excel import functionality',
          'Administrator data locking',
        ],
        mockup: 'table',
      },
      downtime: {
        title: 'Downtime Management',
        desc: 'Captures symptoms and root causes separately to ensure data integrity and enable accurate analysis. Standardised reporting with comprehensive analytics for reliability engineering.',
        items: [
          'Separate symptom and root cause capture',
          'Traditional & advanced reliability analysis',
          'Standardised reporting views',
          'Integrated reliability metrics',
          'Data integrity by design',
        ],
        mockup: 'stats',
      },
      ml: {
        title: 'Machine Learning & AI',
        desc: 'Digital twins, outlier detection, and forecasting to predict mass pulls, recoveries, and upgrade ratios from feed grades and process parameters.',
        items: [
          'Digital twins of plant processes',
          'Outlier detection and anomaly alerts',
          'Recovery and grade forecasting',
          'Correlation analysis across parameters',
          'Control charts and histograms',
        ],
        mockup: 'chart',
      },
      compliance: {
        title: 'Metal Compliance',
        desc: 'Comprehensive monitoring and tracking from annual compliance audits to daily measurements. Track compliance and risk scores over time with full document management.',
        items: [
          'Annual audit to daily measurement tracking',
          'Equipment calibration & certification management',
          'Automatic expiry notifications',
          'Compliance and risk score trending',
          'Document upload, approval, and rejection',
          'Real-time compliance status dashboard',
        ],
        mockup: 'compliance',
      },
      reports: {
        title: 'Reports & Notifications',
        desc: 'Automated reporting through multiple channels — Telegram, WhatsApp, email, and web. Event-based notifications keep your team informed with proactive action alerts.',
        items: [
          'Telegram, WhatsApp, and email delivery',
          'Automated PDF reports from dashboards',
          'Event-based notifications',
          'Proactive action alerts',
          'Broadcast at specific intervals or times',
        ],
        mockup: 'stats',
      },
    };

    const p = panels[this._activeTab];
    return html`
      <div class="feature-panel">
        <div class="feature-info">
          <h3>${p.title}</h3>
          <p class="feature-desc">${p.desc}</p>
          <ul class="feature-list">
            ${p.items.map(item => html`<li>${item}</li>`)}
          </ul>
        </div>
        <div class="feature-mockup">
          <div class="mockup-header">
            <div class="dot dot-r"></div>
            <div class="dot dot-y"></div>
            <div class="dot dot-g"></div>
          </div>
          ${this._renderMockup(p.mockup)}
        </div>
      </div>
    `;
  }

  _renderMockup(type) {
    if (type === 'chart') {
      const heights = [35, 55, 45, 70, 50, 85, 60, 75, 40, 90, 65, 80, 48, 72, 58];
      return html`
        <div class="mockup-body">
          <div class="mockup-chart">
            ${heights.map((h, i) => html`
              <div class="chart-bar ${i % 2 === 0 ? 'purple' : 'light'}" style="height: ${h}%"></div>
            `)}
          </div>
          <div class="mockup-stats">
            <div class="stat-box"><div class="stat-val">98.4%</div><div class="stat-lbl">Recovery</div></div>
            <div class="stat-box"><div class="stat-val">1.4k</div><div class="stat-lbl">Sensors</div></div>
            <div class="stat-box"><div class="stat-val">24/7</div><div class="stat-lbl">Monitoring</div></div>
          </div>
        </div>
      `;
    }

    if (type === 'table') {
      return html`
        <div class="mockup-body">
          <table class="mockup-table">
            <thead><tr><th>Stream</th><th>Actual</th><th>Budget</th><th>Variance</th></tr></thead>
            <tbody>
              <tr><td>Feed Grade</td><td>3.42 g/t</td><td>3.50 g/t</td><td class="val-amber">-2.3%</td></tr>
              <tr><td>Recovery</td><td>94.8%</td><td>93.0%</td><td class="val-green">+1.9%</td></tr>
              <tr><td>Throughput</td><td>245 kt</td><td>240 kt</td><td class="val-green">+2.1%</td></tr>
              <tr><td>Au Produced</td><td>792 oz</td><td>780 oz</td><td class="val-green">+1.5%</td></tr>
              <tr><td>Concentrate</td><td>18.4 t</td><td>18.0 t</td><td class="val-green">+2.2%</td></tr>
            </tbody>
          </table>
          <div class="mockup-stats" style="margin-top: 0.75rem;">
            <div class="stat-box"><div class="stat-val">+1.9%</div><div class="stat-lbl">Recovery vs Budget</div></div>
            <div class="stat-box"><div class="stat-val">792</div><div class="stat-lbl">Ounces Produced</div></div>
            <div class="stat-box"><div class="stat-val">245kt</div><div class="stat-lbl">Throughput</div></div>
          </div>
        </div>
      `;
    }

    if (type === 'compliance') {
      return html`
        <div class="mockup-body">
          <div class="mockup-compliance">
            <div class="compliance-item"><div class="compliance-dot pass"></div><span class="compliance-label">Weighbridge Cal.</span></div>
            <div class="compliance-item"><div class="compliance-dot pass"></div><span class="compliance-label">Lab Scale Cal.</span></div>
            <div class="compliance-item"><div class="compliance-dot warn"></div><span class="compliance-label">ICP Certification</span></div>
            <div class="compliance-item"><div class="compliance-dot pass"></div><span class="compliance-label">XRF Calibration</span></div>
            <div class="compliance-item"><div class="compliance-dot fail"></div><span class="compliance-label">Flowmeter Insp.</span></div>
            <div class="compliance-item"><div class="compliance-dot pass"></div><span class="compliance-label">Belt Scale Cal.</span></div>
          </div>
          <div class="mockup-stats" style="margin-top: 0.75rem;">
            <div class="stat-box"><div class="stat-val">83%</div><div class="stat-lbl">Compliance Score</div></div>
            <div class="stat-box"><div class="stat-val">2</div><div class="stat-lbl">Expiring Soon</div></div>
            <div class="stat-box"><div class="stat-val">1</div><div class="stat-lbl">Action Required</div></div>
          </div>
        </div>
      `;
    }

    return html`
      <div class="mockup-body">
        <div class="mockup-stats">
          <div class="stat-box"><div class="stat-val">96.3%</div><div class="stat-lbl">Availability</div></div>
          <div class="stat-box"><div class="stat-val">4.2h</div><div class="stat-lbl">MTTR</div></div>
          <div class="stat-box"><div class="stat-val">187h</div><div class="stat-lbl">MTBF</div></div>
        </div>
        <div class="mockup-chart" style="height: 100px;">
          ${[60, 30, 75, 20, 85, 45, 90, 35, 70, 55, 40, 80].map((h, i) => html`
            <div class="chart-bar ${i % 3 === 0 ? 'purple' : 'light'}" style="height: ${h}%"></div>
          `)}
        </div>
        <div style="display: flex; gap: 0.5rem;">
          <div class="mockup-bar accent" style="flex: 3; height: 6px;"></div>
          <div class="mockup-bar" style="flex: 1; height: 6px;"></div>
          <div class="mockup-bar accent" style="flex: 2; height: 6px;"></div>
        </div>
      </div>
    `;
  }

  render() {
    return html`
      <!-- HERO -->
      <section class="hero" id="metalyx">
        <div class="hero-bg"></div>
        <div class="hero-grid"></div>
        <div class="hero-content">
          <div class="hero-icon">
            <svg viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="hero-oTopL" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#7C3AED"/><stop offset="100%" stop-color="#6D28D9"/>
                </linearGradient>
                <linearGradient id="hero-oTopR" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#6D28D9"/><stop offset="100%" stop-color="#5B21B6"/>
                </linearGradient>
                <linearGradient id="hero-iTop" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#DDD6FE"/><stop offset="100%" stop-color="#C4B5FD"/>
                </linearGradient>
                <filter id="hero-glow"><feGaussianBlur stdDeviation="6" result="blur"/><feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge></filter>
              </defs>
              <polygon points="256,80 416,168 256,256" fill="url(#hero-oTopR)"/>
              <polygon points="256,80 96,168 256,256" fill="url(#hero-oTopL)"/>
              <polygon points="96,168 256,256 96,344" fill="#4C1D95"/>
              <polygon points="256,256 256,432 96,344" fill="#3B0764"/>
              <polygon points="416,168 256,256 416,344" fill="#6D28D9"/>
              <polygon points="256,256 256,432 416,344" fill="#5B21B6"/>
              <g stroke="#8B5CF6" stroke-width="1" fill="none" opacity="0.25">
                <line x1="256" y1="80" x2="256" y2="256"/>
                <line x1="96" y1="168" x2="256" y2="432"/>
                <line x1="256" y1="256" x2="96" y2="344"/>
                <line x1="416" y1="168" x2="256" y2="432"/>
                <line x1="256" y1="256" x2="416" y2="344"/>
              </g>
              <g filter="url(#hero-glow)">
                <polygon points="256,155 336,199 256,243 176,199" fill="url(#hero-iTop)"/>
                <polygon points="176,199 256,243 256,331 176,287" fill="#A78BFA"/>
                <polygon points="336,199 256,243 256,331 336,287" fill="#C4B5FD"/>
              </g>
              <g fill="#FFFFFF" opacity="0.7">
                <circle cx="256" cy="155" r="3"/><circle cx="218" cy="176" r="1.8"/><circle cx="300" cy="212" r="1.5"/>
              </g>
            </svg>
          </div>
          <div class="hero-divider"></div>
          <h1 class="hero-title">
            <span class="gradient-text">Metalyx</span><br/>
            Platform
          </h1>
          <p class="hero-tagline">
            Empowering the mining industry with innovative, affordable data management
            and analytics solutions that drive measurable operational impact.
          </p>
          <div class="hero-ctas">
            <metalyx-button href="#contact">Request a Demo</metalyx-button>
            <metalyx-button href="#mt-features" variant="secondary">Explore Features</metalyx-button>
          </div>
        </div>
      </section>

      <div class="divider"></div>

      <!-- MISSION & DIFFERENTIATORS -->
      <div class="section">
        <p class="mission">
          Transforming mining & metallurgy through powerful yet affordable data management
          and analytics solutions that drive measurable operational impact and performance.
        </p>

        <div class="section-label">The Metalyx Approach</div>
        <h2 class="section-title">From Raw Data to Results</h2>
        <div class="gradient-line"></div>
        <div class="section-subtitle">
          Six interconnected pillars that form a complete data value chain.
          Click each side to explore.
        </div>

        <div class="pillars-layout">
          <div class="cube-wrap">
            <svg class="cube-svg" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="p-oTopL" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#7C3AED"/><stop offset="100%" stop-color="#6D28D9"/>
                </linearGradient>
                <linearGradient id="p-oTopR" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#6D28D9"/><stop offset="100%" stop-color="#5B21B6"/>
                </linearGradient>
                <linearGradient id="p-oLeftT" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#4C1D95"/><stop offset="100%" stop-color="#3B0764"/>
                </linearGradient>
                <linearGradient id="p-oLeftB" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#3B0764"/><stop offset="100%" stop-color="#2E1065"/>
                </linearGradient>
                <linearGradient id="p-oRightT" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#6D28D9"/><stop offset="100%" stop-color="#5B21B6"/>
                </linearGradient>
                <linearGradient id="p-oRightB" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stop-color="#5B21B6"/><stop offset="100%" stop-color="#4C1D95"/>
                </linearGradient>
                <linearGradient id="p-iTop" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stop-color="#DDD6FE"/><stop offset="100%" stop-color="#C4B5FD"/>
                </linearGradient>
                <filter id="p-glow">
                  <feGaussianBlur stdDeviation="6" result="blur"/>
                  <feMerge><feMergeNode in="blur"/><feMergeNode in="SourceGraphic"/></feMerge>
                </filter>
                <filter id="p-shadow">
                  <feDropShadow dx="0" dy="6" stdDeviation="10" flood-color="#1E0538" flood-opacity="0.5"/>
                </filter>
              </defs>

              <!-- Outer cube facets — each is a clickable pillar -->
              <g filter="url(#p-shadow)">
                <!-- 01 Collect: top-left facet -->
                <g class="cube-facet ${this._activePillar === 'collect' ? 'active' : ''}" @click="${() => this._setPillar('collect')}">
                  <polygon class="facet-fill" points="256,80 96,168 256,256" fill="url(#p-oTopL)"/>
                  <polygon class="facet-overlay" points="256,80 96,168 256,256"/>
                  <text class="cube-num" x="170" y="155">01</text>
                  <text class="cube-label" x="170" y="170">Collect</text>
                </g>

                <!-- 02 Integrate: top-right facet (clockwise from top-left) -->
                <g class="cube-facet ${this._activePillar === 'integrate' ? 'active' : ''}" @click="${() => this._setPillar('integrate')}">
                  <polygon class="facet-fill" points="256,80 416,168 256,256" fill="url(#p-oTopR)"/>
                  <polygon class="facet-overlay" points="256,80 416,168 256,256"/>
                  <text class="cube-num" x="342" y="155">02</text>
                  <text class="cube-label" x="342" y="170">Integrate</text>
                </g>

                <!-- 03 Process: right-upper facet (clockwise continues down right) -->
                <g class="cube-facet ${this._activePillar === 'process' ? 'active' : ''}" @click="${() => this._setPillar('process')}">
                  <polygon class="facet-fill" points="416,168 256,256 416,344" fill="url(#p-oRightT)"/>
                  <polygon class="facet-overlay" points="416,168 256,256 416,344"/>
                  <text class="cube-num" x="370" y="253">03</text>
                  <text class="cube-label" x="370" y="268">Process</text>
                </g>

                <!-- 04 Analyse: right-lower facet -->
                <g class="cube-facet ${this._activePillar === 'analyse' ? 'active' : ''}" @click="${() => this._setPillar('analyse')}">
                  <polygon class="facet-fill" points="256,256 256,432 416,344" fill="url(#p-oRightB)"/>
                  <polygon class="facet-overlay" points="256,256 256,432 416,344"/>
                  <text class="cube-num" x="330" y="348">04</text>
                  <text class="cube-label" x="330" y="363">Analyse</text>
                </g>

                <!-- 05 Deliver: left-lower facet (clockwise wraps to bottom-left) -->
                <g class="cube-facet ${this._activePillar === 'deliver' ? 'active' : ''}" @click="${() => this._setPillar('deliver')}">
                  <polygon class="facet-fill" points="256,256 256,432 96,344" fill="url(#p-oLeftB)"/>
                  <polygon class="facet-overlay" points="256,256 256,432 96,344"/>
                  <text class="cube-num" x="182" y="348">05</text>
                  <text class="cube-label" x="182" y="363">Deliver</text>
                </g>

                <!-- 06 Optimise: left-upper facet (clockwise back up left) -->
                <g class="cube-facet ${this._activePillar === 'optimise' ? 'active' : ''}" @click="${() => this._setPillar('optimise')}">
                  <polygon class="facet-fill" points="96,168 256,256 96,344" fill="url(#p-oLeftT)"/>
                  <polygon class="facet-overlay" points="96,168 256,256 96,344"/>
                  <text class="cube-num" x="142" y="253">06</text>
                  <text class="cube-label" x="142" y="268">Optimise</text>
                </g>
              </g>

              <!-- Inner cube (the gem) - rendered before labels so labels are on top -->
              <g filter="url(#p-glow)">
                <polygon points="256,155 336,199 256,243 176,199" fill="url(#p-iTop)"/>
                <polygon points="176,199 256,243 256,331 176,287" fill="#A78BFA"/>
                <polygon points="336,199 256,243 256,331 336,287" fill="#C4B5FD"/>
              </g>
              <g stroke="#EDE9FE" stroke-width="0.7" fill="none" opacity="0.45">
                <line x1="256" y1="155" x2="256" y2="243"/>
                <line x1="176" y1="199" x2="256" y2="331"/>
                <line x1="336" y1="199" x2="256" y2="331"/>
              </g>
              <g stroke="#EDE9FE" stroke-width="1.3" fill="none" opacity="0.75" stroke-linejoin="round">
                <polygon points="256,155 336,199 256,243 176,199"/>
                <line x1="176" y1="199" x2="176" y2="287"/>
                <line x1="336" y1="199" x2="336" y2="287"/>
                <line x1="256" y1="243" x2="256" y2="331"/>
                <line x1="176" y1="287" x2="256" y2="331"/>
                <line x1="336" y1="287" x2="256" y2="331"/>
              </g>
              <g fill="#FFFFFF" opacity="0.7">
                <circle cx="256" cy="155" r="3"/><circle cx="218" cy="176" r="1.8"/>
                <circle cx="300" cy="212" r="1.5"/><circle cx="256" cy="198" r="1"/>
              </g>

              <!-- Facet seam lines -->
              <g stroke="#8B5CF6" stroke-width="1" fill="none" opacity="0.25">
                <line x1="256" y1="80" x2="256" y2="256"/>
                <line x1="96" y1="168" x2="256" y2="432"/>
                <line x1="256" y1="256" x2="96" y2="344"/>
                <line x1="416" y1="168" x2="256" y2="432"/>
                <line x1="256" y1="256" x2="416" y2="344"/>
              </g>

              <!-- Outer cube edges -->
              <g stroke="#A78BFA" stroke-width="1.5" fill="none" opacity="0.45" stroke-linejoin="round">
                <polygon points="256,80 416,168 256,256 96,168"/>
                <line x1="96" y1="168" x2="96" y2="344"/>
                <line x1="416" y1="168" x2="416" y2="344"/>
                <line x1="256" y1="256" x2="256" y2="432"/>
                <line x1="96" y1="344" x2="256" y2="432"/>
                <line x1="416" y1="344" x2="256" y2="432"/>
              </g>

            </svg>
          </div>

          <div class="pillar-detail">
            <div class="pillar-detail-num">${this._pillarData[this._activePillar].num}</div>
            <div class="pillar-detail-title">${this._pillarData[this._activePillar].title}</div>
            <p class="pillar-detail-desc">${this._pillarData[this._activePillar].desc}</p>
            <ul class="pillar-detail-list">
              ${this._pillarData[this._activePillar].items.map(item => html`<li>${item}</li>`)}
            </ul>
          </div>
        </div>

        <div style="margin-bottom: 4rem;"></div>

        <div class="section-label">What Sets Us Apart</div>
        <div class="gradient-line"></div>
        <div style="margin-bottom: 2.5rem;"></div>

        <div class="diff-grid">
          <div class="diff-card">
            <div class="diff-icon"><metalyx-icon name="analytics" size="20"></metalyx-icon></div>
            <div class="diff-title">Built by Mining Professionals</div>
            <div class="diff-text">Designed and supported by mining & metallurgical experts who understand your operations.</div>
          </div>
          <div class="diff-card">
            <div class="diff-icon"><metalyx-icon name="decision" size="20"></metalyx-icon></div>
            <div class="diff-title">Affordable Solutions</div>
            <div class="diff-text">Cost effective compared to competitors without compromising on capability.</div>
          </div>
          <div class="diff-card">
            <div class="diff-icon"><metalyx-icon name="predict" size="20"></metalyx-icon></div>
            <div class="diff-title">Maximum Impact</div>
            <div class="diff-text">Delivering real value with maximum impact on your bottom line.</div>
          </div>
          <div class="diff-card">
            <div class="diff-icon"><metalyx-icon name="brain" size="20"></metalyx-icon></div>
            <div class="diff-title">Simplicity</div>
            <div class="diff-text">Keeping things real and simple. Make big data small again.</div>
          </div>
          <div class="diff-card">
            <div class="diff-icon"><metalyx-icon name="integrate" size="20"></metalyx-icon></div>
            <div class="diff-title">Flexible Implementation</div>
            <div class="diff-text">Solutions that adapt to your needs and scale with your growth.</div>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- OFFERINGS -->
      <div class="section-alt">
        <div class="section-inner">
          <div class="section-label">Our Offerings</div>
          <h2 class="section-title">Four Pillars of<br/>Operational Excellence</h2>
          <div class="gradient-line"></div>
          <div class="section-subtitle">
            End-to-end solutions for mining and metallurgical operations — from
            automated accounting to machine learning.
          </div>

          <div class="offerings-grid">
            <div class="offering-card">
              <div class="offering-num">01</div>
              <div class="offering-title">Business Intelligence</div>
              <div class="offering-text">Comprehensive reporting and analytics platform combining real-time and historical data across all operational areas.</div>
            </div>
            <div class="offering-card">
              <div class="offering-num">02</div>
              <div class="offering-title">AI Solutions</div>
              <div class="offering-text">Machine learning models that solve complex metallurgical challenges and optimise process performance.</div>
            </div>
            <div class="offering-card">
              <div class="offering-num">03</div>
              <div class="offering-title">Optimisation</div>
              <div class="offering-text">Technology solutions enhanced through strategic partnerships for operational excellence and continuous improvement.</div>
            </div>
            <div class="offering-card">
              <div class="offering-num">04</div>
              <div class="offering-title">Metal Accounting</div>
              <div class="offering-text">Fully automated metal accounting solutions for the metallurgical industry. Track from mine to market with real-time reconciliations.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- PLATFORM ARCHITECTURE -->
      <div class="section" id="mt-arch">
        <div class="section-label">Platform Architecture</div>
        <h2 class="section-title">From Data Sources to Decisions</h2>
        <div class="gradient-line"></div>
        <div class="section-subtitle">
          A unified platform that connects your operational data sources, processes them
          through powerful engines, and delivers actionable insights.
        </div>

        <div class="arch-container">
          <div class="arch-col">
            <div class="arch-col-label sources">Data Sources</div>
            <div class="arch-group">
              <div class="arch-group-label">Mining</div>
              ${[
                { name: 'Drilling & Blasting', color: 'light' },
                { name: 'Dispatch / FMS', color: 'light' },
                { name: 'Survey & Geology', color: 'mid' },
              ].map(s => html`
                <div class="arch-item"><span class="arch-dot ${s.color}"></span>${s.name}</div>
              `)}
            </div>
            <div class="arch-group">
              <div class="arch-group-label">Processing</div>
              ${[
                { name: 'Historians / SCADA', color: 'primary' },
                { name: 'Lab / LIMS', color: 'primary' },
                { name: 'Weighbridge', color: 'mid' },
              ].map(s => html`
                <div class="arch-item"><span class="arch-dot ${s.color}"></span>${s.name}</div>
              `)}
            </div>
            <div class="arch-group">
              <div class="arch-group-label">General</div>
              ${[
                { name: 'ERP / Finance', color: 'deep' },
                { name: 'Manual Data', color: 'deep' },
              ].map(s => html`
                <div class="arch-item"><span class="arch-dot ${s.color}"></span>${s.name}</div>
              `)}
            </div>
          </div>

          <div class="arch-arrows">
            <span class="arch-arrow-icon">&rsaquo;</span>
            <span class="arch-arrow-icon">&rsaquo;</span>
            <span class="arch-arrow-icon">&rsaquo;</span>
          </div>

          <div class="arch-core">
            <div class="arch-core-header">
              <svg class="arch-core-logo" viewBox="0 0 512 512" xmlns="http://www.w3.org/2000/svg">
                <polygon points="256,80 416,168 256,256" fill="#6D28D9"/>
                <polygon points="256,80 96,168 256,256" fill="#7C3AED"/>
                <polygon points="96,168 256,256 96,344" fill="#4C1D95"/>
                <polygon points="256,256 256,432 96,344" fill="#3B0764"/>
                <polygon points="416,168 256,256 416,344" fill="#6D28D9"/>
                <polygon points="256,256 256,432 416,344" fill="#5B21B6"/>
                <polygon points="256,155 336,199 256,243 176,199" fill="#DDD6FE"/>
                <polygon points="176,199 256,243 256,331 176,287" fill="#A78BFA"/>
                <polygon points="336,199 256,243 256,331 336,287" fill="#C4B5FD"/>
              </svg>
              <div class="arch-core-title">Metalyx Platform</div>
            </div>
            <div class="arch-core-grid">
              ${['Data Warehouse', 'Web Platform', 'Calc Engine', 'Event Manager', 'Config Manager'].map(c => html`
                <div class="arch-core-item">${c}</div>
              `)}
            </div>
          </div>

          <div class="arch-arrows">
            <span class="arch-arrow-icon">&rsaquo;</span>
            <span class="arch-arrow-icon">&rsaquo;</span>
            <span class="arch-arrow-icon">&rsaquo;</span>
          </div>

          <div class="arch-col">
            <div class="arch-col-label outputs">Outputs</div>
            ${[
              { name: 'Dashboards', color: 'deep' },
              { name: 'Metal Accounting', color: 'deep' },
              { name: 'Downtime Mgmt', color: 'primary' },
              { name: 'Reports', color: 'primary' },
              { name: 'ML Models', color: 'mid' },
              { name: 'Notifications', color: 'mid' },
              { name: 'Compliance', color: 'light' },
              { name: 'Process Diary', color: 'light' },
            ].map(o => html`
              <div class="arch-item"><span class="arch-dot ${o.color}"></span>${o.name}</div>
            `)}
          </div>
        </div>

        <div class="arch-flow-strip">
          <div class="arch-flow-label">
            <span class="flow-icon">&#9673;</span>Collect
          </div>
          <span class="arch-flow-chevron">&#10145;</span>
          <div class="arch-flow-label">
            <span class="flow-icon">&#9876;</span>Integrate
          </div>
          <span class="arch-flow-chevron">&#10145;</span>
          <div class="arch-flow-label active">
            <span class="flow-icon">&#9881;</span>Process
          </div>
          <span class="arch-flow-chevron">&#10145;</span>
          <div class="arch-flow-label">
            <span class="flow-icon">&#9672;</span>Analyse
          </div>
          <span class="arch-flow-chevron">&#10145;</span>
          <div class="arch-flow-label">
            <span class="flow-icon">&#9889;</span>Deliver
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- FEATURE MODULES -->
      <div class="section-alt" id="mt-features">
        <div class="section-inner">
          <div class="section-label">Feature Modules</div>
          <h2 class="section-title">Everything You Need,<br/>Nothing You Don't</h2>
          <div class="gradient-line"></div>
          <div class="section-subtitle">
            Purpose-built modules that work together as an integrated platform or
            independently based on your requirements.
          </div>

          <div class="feature-tabs">
            ${['dashboards', 'accounting', 'downtime', 'ml', 'compliance', 'reports'].map(tab => html`
              <button
                class="feature-tab ${this._activeTab === tab ? 'active' : ''}"
                @click="${() => this._setTab(tab)}"
              >
                ${{
                  dashboards: 'Live Dashboards',
                  accounting: 'Metal Accounting',
                  downtime: 'Downtime',
                  ml: 'AI & ML',
                  compliance: 'Compliance',
                  reports: 'Reports',
                }[tab]}
              </button>
            `)}
          </div>

          ${this._renderFeaturePanel()}
        </div>
      </div>

      <div class="divider"></div>

      <!-- BENEFITS -->
      <div class="section" id="mt-benefits">
        <div class="section-label">Why Metalyx</div>
        <h2 class="section-title">Measurable Results</h2>
        <div class="gradient-line"></div>
        <div class="section-subtitle">
          Real value delivered to mining operations worldwide.
        </div>

        <div class="benefits-grid">
          <div class="benefit-card">
            <div class="benefit-icon">&#9678;</div>
            <div class="benefit-title">Single Source of Truth</div>
            <div class="benefit-text">Unify all operational data into one authoritative platform.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#9670;</div>
            <div class="benefit-title">Process Analysis</div>
            <div class="benefit-text">Identify bottlenecks and optimise performance across your plant.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#9881;</div>
            <div class="benefit-title">Automate Accounting</div>
            <div class="benefit-text">Eliminate manual reconciliations with automated metal accounting.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#9673;</div>
            <div class="benefit-title">Enhanced Visibility</div>
            <div class="benefit-text">Real-time dashboards give every stakeholder the data they need.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#10070;</div>
            <div class="benefit-title">Hidden Patterns</div>
            <div class="benefit-text">Machine learning discovers correlations humans miss in complex data.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#9202;</div>
            <div class="benefit-title">Save Time & Resources</div>
            <div class="benefit-text">Automate reporting and data capture to free your team for higher-value work.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#9745;</div>
            <div class="benefit-title">Fact-Based Decisions</div>
            <div class="benefit-text">Move from gut-feel to data-driven decision-making at every level.</div>
          </div>
          <div class="benefit-card">
            <div class="benefit-icon">&#9883;</div>
            <div class="benefit-title">Scalable Growth</div>
            <div class="benefit-text">Start with what you need today — the platform grows with your operation.</div>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- IMPLEMENTATION TIMELINE -->
      <div class="section-alt">
        <div class="section-inner">
          <div class="section-label">Implementation</div>
          <h2 class="section-title">Agile Delivery, Early Results</h2>
          <div class="gradient-line"></div>
          <div class="section-subtitle">
            Quick sprints bring early results while steadily increasing value over time.
          </div>

          <div class="timeline">
            <div class="timeline-step">
              <div class="timeline-dot"></div>
              <div class="timeline-week">Week 1 – 3</div>
              <div class="timeline-title">Discovery</div>
              <ul class="timeline-items">
                <li>Data source mapping</li>
                <li>Quick wins identified</li>
                <li>Metal accounting processes mapped</li>
              </ul>
            </div>
            <div class="timeline-step">
              <div class="timeline-dot"></div>
              <div class="timeline-week">Week 4 – 6</div>
              <div class="timeline-title">Basic Integration</div>
              <ul class="timeline-items">
                <li>Weighbridge integration</li>
                <li>LIMS integration</li>
                <li>Historian integration</li>
                <li>Basic measures</li>
              </ul>
            </div>
            <div class="timeline-step">
              <div class="timeline-dot"></div>
              <div class="timeline-week">Week 7 – 12</div>
              <div class="timeline-title">Advanced Automation</div>
              <ul class="timeline-items">
                <li>Advanced metal accounting</li>
                <li>Downtime management</li>
                <li>Manual data capture</li>
                <li>Basic dashboards</li>
              </ul>
            </div>
            <div class="timeline-step">
              <div class="timeline-dot"></div>
              <div class="timeline-week">Week 13 – 16</div>
              <div class="timeline-title">Full Deployment</div>
              <ul class="timeline-items">
                <li>Advanced reconciliation</li>
                <li>Advanced dashboards</li>
                <li>Automated reports</li>
                <li>Additional requirements</li>
              </ul>
            </div>
            <div class="timeline-step">
              <div class="timeline-dot"></div>
              <div class="timeline-week">Week 17+</div>
              <div class="timeline-title">Continuous Enhancement</div>
              <ul class="timeline-items">
                <li>Machine learning models</li>
                <li>Digital twins</li>
                <li>Continuous monitoring</li>
                <li>Documentation</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- BESPOKE SOLUTIONS -->
      <div class="section">
        <div class="section-label">Beyond the Platform</div>
        <h2 class="section-title">Bespoke Solutions</h2>
        <div class="gradient-line"></div>
        <div class="section-subtitle">
          When off-the-shelf doesn't fit, we build custom solutions tailored
          to your unique operational challenges.
        </div>

        <div class="bespoke-layout">
          <div class="bespoke-text">
            <p>
              Every mining operation is different. Our team of <strong>mining engineers,
              metallurgists, and data scientists</strong> work alongside your team to design
              and deliver custom solutions that address your specific challenges.
            </p>
            <p>
              From <strong>custom operational management systems</strong> to
              <strong>advanced data science models</strong>, we build what you need —
              whether it's a standalone application or an extension of the Metalyx platform.
            </p>
            <p>
              We handle the full lifecycle: requirements gathering, design, development,
              deployment, and ongoing support — so you can focus on running your operation.
            </p>
          </div>

          <div class="bespoke-cards">
            <div class="bespoke-card">
              <div class="bespoke-card-icon">&#9881;</div>
              <div class="bespoke-card-title">Custom Applications</div>
              <div class="bespoke-card-text">Purpose-built web and mobile apps for operational workflows unique to your site.</div>
            </div>
            <div class="bespoke-card">
              <div class="bespoke-card-icon">&#9672;</div>
              <div class="bespoke-card-title">Advanced Analytics</div>
              <div class="bespoke-card-text">Tailored data science models, digital twins, and predictive algorithms for your process.</div>
            </div>
            <div class="bespoke-card">
              <div class="bespoke-card-icon">&#9876;</div>
              <div class="bespoke-card-title">System Integration</div>
              <div class="bespoke-card-text">Connect disparate systems, automate data flows, and unify your operational data landscape.</div>
            </div>
            <div class="bespoke-card">
              <div class="bespoke-card-icon">&#9670;</div>
              <div class="bespoke-card-title">Process Optimisation</div>
              <div class="bespoke-card-text">Data-driven studies and tools to identify inefficiencies and maximise throughput and recovery.</div>
            </div>
          </div>
        </div>
      </div>

      <div class="divider"></div>

      <!-- CLIENTS -->
      <div class="section">
        <div class="section-label">Trusted By</div>
        <h2 class="section-title">Our Clients</h2>
        <div class="gradient-line"></div>
        <div class="section-subtitle">
          Partnering with leading mining and minerals operations across Africa.
        </div>

        <div class="clients-grid">
          <img class="client-logo" src="images/northan-eland-logo.png" alt="Northam Eland" />
          <img class="client-logo" src="images/mbcc-logo.png" alt="MBCC" />
          <img class="client-logo" src="images/valterra-platinum-logo.svg" alt="Valterra Platinum" />
          <img class="client-logo" src="images/xtropy-logo.svg" alt="Xtropy" />
        </div>
      </div>

      <!-- CTA -->
      <div class="cta-section">
        <div class="cta-bg"></div>
        <div class="cta-content">
          <div class="gradient-line"></div>
          <h2 class="cta-title">Ready to Transform<br/>Your Operation?</h2>
          <p class="cta-text">
            See how Metalyx can drive measurable impact at your mine or plant.
            Request a demo and we'll walk you through the platform.
          </p>
          <metalyx-button href="#contact">Request a Demo</metalyx-button>
        </div>
      </div>
    `;
  }
}

customElements.define('metalyx-page', MetalyxPage);